import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../Common/Card';
import Button from '../Common/Button';
import EquipmentBrowser from './EquipmentBrowser';
import SpellBrowser from './SpellBrowser';
import { LootConsumableBrowser } from './LootConsumableBrowser';
import { CharacterData } from '../../types/character';
import { itemLoader } from '../../utils/itemLoader';
import { isConsumableItem } from '../../types/equipment';
import './ContentLibrary.css';

type LibraryTab = 'weapons' | 'armor' | 'wheelchairs' | 'loot' | 'consumables' | 'spells';

interface ContentLibraryProps {
  characterId?: string;
  character?: CharacterData | null;
  baseCharacter?: CharacterData | null; // For duplicate detection
  onAddToCharacter?: (itemType: string, itemId: string) => Promise<void>;
  onRemoveFromCharacter?: (itemType: string, itemId: string) => Promise<void>;
}

const ContentLibrary: React.FC<ContentLibraryProps> = ({
  characterId,
  character,
  baseCharacter,
  onAddToCharacter,
  onRemoveFromCharacter,
}) => {
  const [activeTab, setActiveTab] = useState<LibraryTab>('weapons');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileNavRef = useRef<HTMLDivElement>(null);

  const tabs = [
    { id: 'weapons' as LibraryTab, label: 'Weapons', icon: '⚔️' },
    { id: 'armor' as LibraryTab, label: 'Armor', icon: '🛡️' },
    { id: 'wheelchairs' as LibraryTab, label: 'Wheelchairs', icon: '♿' },
    { id: 'loot' as LibraryTab, label: 'Loot', icon: '💎' },
    { id: 'consumables' as LibraryTab, label: 'Consumables', icon: '🧪' },
    { id: 'spells' as LibraryTab, label: 'Domain Cards', icon: '🃏' },
  ];

  const handleAddToCharacter = async (itemType: string, itemId: string) => {
    if (onAddToCharacter && characterId) {
      await onAddToCharacter(itemType, itemId);
    }
  };

  const handleRemoveFromCharacter = async (itemType: string, itemId: string) => {
    if (onRemoveFromCharacter && characterId) {
      await onRemoveFromCharacter(itemType, itemId);
    }
  };

  // Handler for loot/consumable items (only needs itemId)
  const handleAddItemToInventory = async (itemId: string) => {
    if (onAddToCharacter && characterId) {
      // Determine item type
      const item = itemLoader.findItemById(itemId);
      if (!item) return;

      const itemType = isConsumableItem(item) ? 'consumable' : 'loot';
      await onAddToCharacter(itemType, itemId);
    }
  };

  const handleTabChange = (tabId: LibraryTab) => {
    setActiveTab(tabId);
    setIsMobileMenuOpen(false); // Close mobile menu when tab is selected
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMobileMenuOpen]);

  return (
    <div className="content-library">
      <Card title="Content Library">
        <div className="content-library__content">
          {/* Desktop Tab Navigation */}
          <motion.div 
            className="content-library__tabs content-library__tabs--desktop"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, staggerChildren: 0.1 }}
          >
            {tabs.map((tab, index) => (
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Button
                  variant={activeTab === tab.id ? 'PRIMARY' : 'SECONDARY'}
                  size="MEDIUM"
                  onClick={() => handleTabChange(tab.id)}
                  className="content-library__tab-button"
                >
                  <span className="content-library__tab-icon">{tab.icon}</span>
                  {tab.label}
                </Button>
              </motion.div>
            ))}
          </motion.div>

          {/* Mobile Hamburger Menu */}
          <div className="content-library__mobile-nav" ref={mobileNavRef}>
            <Button
              variant="SECONDARY"
              size="MEDIUM"
              onClick={toggleMobileMenu}
              className="content-library__mobile-toggle"
            >
              <span className="content-library__hamburger">☰</span>
              <span className="content-library__tab-icon">{activeTabData.icon}</span>
              {activeTabData.label}
              <span className={`content-library__dropdown-arrow ${isMobileMenuOpen ? 'content-library__dropdown-arrow--open' : ''}`}>
                ▼
              </span>
            </Button>

            {/* Mobile Dropdown Menu */}
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.div 
                  className="content-library__mobile-menu"
                  initial={{ opacity: 0, height: 0, y: -10 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -10 }}
                  transition={{ duration: 0.2, staggerChildren: 0.03 }}
                >
                  {tabs.map((tab, index) => (
                    <motion.div
                      key={tab.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.15, delay: index * 0.03 }}
                    >
                      <Button
                        variant={activeTab === tab.id ? 'PRIMARY' : 'SECONDARY'}
                        size="MEDIUM"
                        onClick={() => handleTabChange(tab.id)}
                        className="content-library__mobile-menu-item"
                      >
                        <span className="content-library__tab-icon">{tab.icon}</span>
                        {tab.label}
                      </Button>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Tab Content */}
          <motion.div
            className="content-library__tab-content"
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {(activeTab === 'weapons' || activeTab === 'armor' || activeTab === 'wheelchairs') && (
              <EquipmentBrowser
                characterId={characterId}
                category={activeTab}
                character={character}
                onAddToCharacter={handleAddToCharacter}
              />
            )}
            {activeTab === 'loot' && character && (
              <LootConsumableBrowser
                character={character}
                onAddToCharacter={handleAddItemToInventory}
                itemType="loot"
              />
            )}
            {activeTab === 'consumables' && character && (
              <LootConsumableBrowser
                character={character}
                onAddToCharacter={handleAddItemToInventory}
                itemType="consumable"
              />
            )}
            {activeTab === 'spells' && (
              <SpellBrowser
                characterId={characterId}
                character={character}
                baseCharacter={baseCharacter}
                onAddToCharacter={handleAddToCharacter}
                onRemoveFromCharacter={handleRemoveFromCharacter}
              />
            )}
          </motion.div>
        </div>
      </Card>
    </div>
  );
};

export default ContentLibrary;